import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} SfProductMediaScrollRefs
 * @property {HTMLElement} stack
 * @property {HTMLElement[]} items
 * @property {HTMLElement} [rail]
 * @property {HTMLElement} [dragbar]
 * @property {HTMLElement[]} [thumbs]
 * @property {HTMLElement} [progressFill]
 */

const MOBILE_QUERY = '(max-width: 900px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const POST_DRAG_GUARD_MS = 150;
const DOUBLE_TAP_MS = 350;

/** @extends {Component<SfProductMediaScrollRefs>} */
class SfProductMediaScroll extends Component {
  requiredRefs = ['stack'];

  #isDragging = false;
  #videoObserver = null;
  /** Keeps only the in-view slides' keyboard zoom buttons in the tab order. */
  #zoomTabObserver = null;
  #justDragged = false;
  #startClientY = 0;
  #startDragY = 0;
  #currentDragY = 0;
  #mobileMedia = window.matchMedia(MOBILE_QUERY);
  #reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
  #dragGuardTimer = 0;
  #touchStartX = 0;
  #touchStartY = 0;
  #touchScrollStart = 0;
  #touchActive = false;
  #touchStartTime = 0;
  /** @type {'horizontal' | 'vertical' | null} */
  #touchIntent = null;
  /** @type {HTMLElement | null} */
  #zoomedItem = null;
  #zoomRaf = 0;
  // Pinch-to-zoom state (mobile, two-finger gesture).
  #pinchActive = false;
  #pinchStartDist = 0;
  #pinchStartScale = 1;
  #pinchScale = 1;
  // One-finger pan while zoomed.
  #panX = 0;
  #panY = 0;
  #panStartX = 0;
  #panStartY = 0;
  #panStartTouchX = 0;
  #panStartTouchY = 0;
  #panActive = false;
  /** @type {HTMLElement | null} */
  #pinchItem = null;
  /** @type {HTMLElement | null} */
  #pinchImage = null;
  // Double-tap detection for mobile tap-zoom.
  #lastTapTime = 0;
  /** @type {HTMLElement | null} */
  #lastTapItem = null;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#mobileMedia.addEventListener('change', this.#onBreakpointChange);
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.#wireMode();
    if (this.classList.contains('sf-pv--zoomable')) this.#wireZoom();
    // this.#maybeAutostartVideo();
    this.#initVideoAutoplay();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mobileMedia.removeEventListener('change', this.#onBreakpointChange);
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#dragGuardTimer);
    cancelAnimationFrame(this.#zoomRaf);
    this.#unwireDesktop();
    this.#unwireMobile();
    this.#unwireZoom();
    if (this.#videoObserver) {
      this.#videoObserver.disconnect();
    }
    this.#abortController.abort();
  }

  #handleVariantUpdate = (event) => {
    const mediaId = event.detail?.resource?.featured_media?.id;
    if (mediaId == null) return;

    const items = this.refs.items;
    if (!items || items.length === 0) return;

    const target = items.find((item) => item.querySelector(`[data-media-id="${mediaId}"]`));
    if (!target) return;

    const behavior = this.#reducedMotionMedia.matches ? 'auto' : 'smooth';
    if (this.#mobileMedia.matches) {
      this.refs.stack.scrollTo({ left: target.offsetLeft, behavior });
    } else {
      this.refs.stack.scrollTo({ top: target.offsetTop, behavior });
    }
  };

  // Thumb click entry point: on:click="/handleThumbClick/N" parses N as a number.
  handleThumbClick = (index) => {
    if (this.#mobileMedia.matches) return;
    if (this.#justDragged) return;
    if (typeof index !== 'number' || Number.isNaN(index)) return;

    const item = this.refs.items?.[index];
    if (!item) return;

    const behavior = this.#reducedMotionMedia.matches ? 'auto' : 'smooth';
    this.refs.stack.scrollTo({ top: item.offsetTop, behavior });
  };

  #wireMode = () => {
    if (this.#mobileMedia.matches) {
      this.#unwireDesktop();
      this.#wireMobile();
    } else {
      this.#unwireMobile();
      this.#wireDesktop();
    }
  };

  #wireDesktop = () => {
    if (!this.refs.rail || !this.refs.dragbar) return;
    this.refs.stack.addEventListener('scroll', this.#onStackScrollDesktop, { passive: true });
    this.refs.dragbar.addEventListener('pointerdown', this.#onPointerDown);
    this.#syncFromStackScrollDesktop();
  };

  #unwireDesktop = () => {
    this.refs.stack.removeEventListener('scroll', this.#onStackScrollDesktop);
    this.refs.dragbar?.removeEventListener('pointerdown', this.#onPointerDown);
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('pointercancel', this.#onPointerUp);
  };

  #wireMobile = () => {
    if (!this.refs.progressFill) return;
    this.refs.stack.addEventListener('scroll', this.#onStackScroll, { passive: true });
    this.refs.stack.addEventListener('touchstart', this.#onMobileTouchStart, { passive: true });
    // Non-passive so we can preventDefault() once horizontal intent is locked,
    // blocking iOS from also pan-scrolling the page during diagonal swipes.
    this.refs.stack.addEventListener('touchmove', this.#onMobileTouchMove, { passive: false });
    this.refs.stack.addEventListener('touchend', this.#onMobileTouchEnd, { passive: true });
    this.refs.stack.addEventListener('touchcancel', this.#onMobileTouchEnd, { passive: true });
    this.#syncFromStackScroll();
  };

  #unwireMobile = () => {
    this.refs.stack.removeEventListener('scroll', this.#onStackScroll);
    this.refs.stack.removeEventListener('touchstart', this.#onMobileTouchStart);
    this.refs.stack.removeEventListener('touchmove', this.#onMobileTouchMove);
    this.refs.stack.removeEventListener('touchend', this.#onMobileTouchEnd);
    this.refs.stack.removeEventListener('touchcancel', this.#onMobileTouchEnd);
  };

  #onBreakpointChange = () => {
    this.#wireMode();
  };

  #onResize = () => {
    if (this.#mobileMedia.matches) {
      this.#syncFromStackScroll();
    } else {
      this.#syncFromStackScrollDesktop();
    }
  };

  #getDesktopMaxScroll = () => {
    const stack = this.refs.stack;
    return Math.max(0, stack.scrollHeight - stack.clientHeight);
  };

  get #maxDrag() {
    const rail = this.refs.rail;
    const dragbar = this.refs.dragbar;
    if (!rail || !dragbar) return 0;
    return Math.max(0, rail.scrollHeight - dragbar.offsetHeight);
  }

  #clamp = (value, max) => Math.max(0, Math.min(max, value));

  #setDragY = (y) => {
    this.#currentDragY = y;
    if (this.refs.dragbar) {
      this.refs.dragbar.style.transform = `translateY(${y}px)`;
    }
  };

  #syncFromStackScrollDesktop = () => {
    if (this.#isDragging) return;
    const maxScroll = this.#getDesktopMaxScroll();
    if (maxScroll === 0) {
      this.#setDragY(0);
      return;
    }
    const ratio = this.#clamp(this.refs.stack.scrollTop / maxScroll, 1);
    this.#setDragY(ratio * this.#maxDrag);
  };

  #onStackScrollDesktop = () => {
    this.#syncFromStackScrollDesktop();
  };

  #onPointerDown = (event) => {
    event.preventDefault();
    this.#isDragging = true;
    this.#startClientY = event.clientY;
    this.#startDragY = this.#currentDragY;
    this.refs.stack.style.scrollSnapType = 'none';
    try {
      this.refs.dragbar?.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    document.addEventListener('pointermove', this.#onPointerMove);
    document.addEventListener('pointerup', this.#onPointerUp);
    document.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (!this.#isDragging) return;
    event.preventDefault();
    const delta = event.clientY - this.#startClientY;
    const maxDrag = this.#maxDrag;
    const newY = this.#clamp(this.#startDragY + delta, maxDrag);
    this.#setDragY(newY);
    if (maxDrag === 0) return;
    const ratio = newY / maxDrag;
    this.refs.stack.scrollTop = ratio * this.#getDesktopMaxScroll();
  };

  #onPointerUp = () => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.#justDragged = true;
    this.refs.stack.style.scrollSnapType = '';
    clearTimeout(this.#dragGuardTimer);
    this.#dragGuardTimer = window.setTimeout(() => {
      this.#justDragged = false;
    }, POST_DRAG_GUARD_MS);
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('pointercancel', this.#onPointerUp);
  };

  // Mobile — horizontal stack scroll drives the progress bar fill

  #onStackScroll = () => {
    this.#syncFromStackScroll();
  };

  #syncFromStackScroll = () => {
    if (!this.refs.progressFill) return;
    const stack = this.refs.stack;
    const total = this.refs.items?.length ?? 0;
    if (total <= 0) {
      this.refs.progressFill.style.width = '0%';
      return;
    }
    if (total === 1) {
      this.refs.progressFill.style.width = '100%';
      return;
    }
    // Fill represents "current slide position out of total slides", so the
    // first slide already shows 1/N filled instead of starting blank.
    const itemWidth = stack.clientWidth || 1;
    const progress = (stack.scrollLeft / itemWidth + 1) / total;
    const ratio = this.#clamp(progress, 1);
    this.refs.progressFill.style.width = `${ratio * 100}%`;
  };

  // Mobile touch drag — horizontal scrolling via JS (overflow: hidden blocks native scroll)
  #onMobileTouchStart = (e) => {
    // While zoomed, don't drive the carousel — let the zoom pan handler own the touch.
    if (this.#zoomedItem) return;

    // Two fingers → pinch zoom. Cancel any in-flight swipe or pan.
    if (e.touches.length >= 2) {
      this.#touchActive = false;
      this.#touchIntent = null;
      this.#panActive = false;
      this.#startPinch(e);
      return;
    }

    // Single finger on a pinch-zoomed item → start pan (or detect tap-to-reset).
    // Only activate pan if the touch is actually ON the zoomed item — touches
    // on other slides (e.g., a video slide) should fall through to slider swipe.
    if (this.#pinchItem && this.#pinchScale > 1) {
      const touchTarget = document.elementFromPoint(
        e.touches[0].clientX,
        e.touches[0].clientY,
      );
      const touchedItem = touchTarget instanceof Element
        ? touchTarget.closest('.sf-pv__item')
        : null;
      if (touchedItem === this.#pinchItem) {
        this.#touchStartX = e.touches[0].clientX;
        this.#touchStartY = e.touches[0].clientY;
        this.#touchActive = true;
        this.#touchStartTime = performance.now();
        this.#touchIntent = 'vertical'; // prevents the slider swipe path
        this.#panActive = true;
        this.#panStartX = this.#panX;
        this.#panStartY = this.#panY;
        this.#panStartTouchX = e.touches[0].clientX;
        this.#panStartTouchY = e.touches[0].clientY;
        this.#pinchItem.dataset.pinching = 'true';
        this.#pinchItem.dataset.pinchZoomed = 'true';
        return;
      }
    }

    this.#touchStartX = e.touches[0].clientX;
    this.#touchStartY = e.touches[0].clientY;
    this.#touchScrollStart = this.refs.stack.scrollLeft;
    this.#touchActive = true;
    this.#touchStartTime = performance.now();
    this.#touchIntent = null;
  };

  #onMobileTouchMove = (e) => {
    if (this.#pinchActive) {
      this.#updatePinch(e);
      return;
    }
    if (this.#panActive) {
      // A second finger landed mid-pan → switch to pinch.
      if (e.touches.length >= 2) {
        this.#panActive = false;
        this.#touchActive = false;
        this.#startPinch(e);
        return;
      }
      this.#updatePan(e);
      return;
    }
    if (!this.#touchActive) return;
    if (this.#zoomedItem) return;

    // A second finger landed mid-swipe → switch to pinch.
    if (e.touches.length >= 2) {
      this.#touchActive = false;
      this.#touchIntent = null;
      this.#startPinch(e);
      return;
    }

    const dx = this.#touchStartX - e.touches[0].clientX;
    const dy = this.#touchStartY - e.touches[0].clientY;

    // Lock the gesture intent on the first move that crosses the threshold so
    // diagonal swipes don't end up scrolling the page AND the carousel.
    if (this.#touchIntent === null) {
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absX < 8 && absY < 8) return;
      this.#touchIntent = absX > absY ? 'horizontal' : 'vertical';
    }

    if (this.#touchIntent === 'vertical') return;

    // Horizontal: block the browser from also panning the page vertically.
    if (e.cancelable) e.preventDefault();
    this.refs.stack.scrollLeft = this.#touchScrollStart + dx;
  };

  #onMobileTouchEnd = (e) => {
    if (this.#pinchActive) {
      // End pinch only when fewer than 2 fingers remain on screen.
      if (e.touches.length < 2) {
        this.#endPinch();
        // If a finger is still on screen and we're still zoomed, hand the
        // gesture off to the pan handler — anchored at that finger's CURRENT
        // position, not the stale touchstart position from before the pinch.
        if (e.touches.length === 1 && this.#pinchItem && this.#pinchScale > 1) {
          this.#panActive = true;
          this.#touchActive = false;
          this.#panStartX = this.#panX;
          this.#panStartY = this.#panY;
          this.#panStartTouchX = e.touches[0].clientX;
          this.#panStartTouchY = e.touches[0].clientY;
          this.#pinchItem.dataset.pinching = 'true';
          this.#pinchItem.dataset.pinchZoomed = 'true';
        }
      }
      return;
    }

    // Single-finger end on a pinch-zoomed item → finalize the pan. The
    // tap-to-reset case is handled by the `click` event handler instead, so
    // it doesn't conflict with #mobileTapZoom which also fires on tap.
    if (this.#panActive) {
      this.#panActive = false;
      this.#touchActive = false;
      this.#touchIntent = null;
      if (this.#pinchItem) {
        delete this.#pinchItem.dataset.pinching;
      }
      return;
    }

    if (!this.#touchActive) return;
    this.#touchActive = false;
    if (this.#zoomedItem) return;
    // Vertical-intent gestures didn't move the carousel — nothing to settle.
    if (this.#touchIntent === 'vertical') {
      this.#touchIntent = null;
      return;
    }
    this.#touchIntent = null;
    const item = this.refs.items?.[0];
    if (!item) return;
    const w = item.offsetWidth;
    if (!w) return;

    const endX = e?.changedTouches?.[0]?.clientX ?? this.#touchStartX;
    const dx = this.#touchStartX - endX; // positive → swiped left (next slide)
    const elapsed = performance.now() - this.#touchStartTime;
    const velocity = elapsed > 0 ? dx / elapsed : 0; // px/ms

    // Current slide index based on where the stack sits (before snap).
    const currentIdx = Math.round(this.#touchScrollStart / w);

    // Trigger a slide change on a small drag (15% of slide width) OR a fast flick
    // (velocity > 0.25 px/ms ≈ 250 px/s). Otherwise snap back to current.
    const distanceThreshold = w * 0.15;
    const velocityThreshold = 0.25;
    let targetIdx = currentIdx;
    if (Math.abs(dx) > distanceThreshold || Math.abs(velocity) > velocityThreshold) {
      targetIdx = currentIdx + (dx > 0 ? 1 : -1);
    }

    const maxIdx = (this.refs.items?.length ?? 1) - 1;
    targetIdx = Math.max(0, Math.min(maxIdx, targetIdx));
    this.refs.stack.scrollTo({ left: targetIdx * w, behavior: 'smooth' });
  };

  // ── Pinch-to-zoom (mobile, two fingers) ────────────────────────

  #pinchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  #startPinch = (e) => {
    if (e.touches.length < 2) return;
    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
    const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    const target = document.elementFromPoint(midX, midY);
    if (!(target instanceof Element)) return;
    const item = target.closest('.sf-pv__item');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;
    // Only zoomable items (images, never videos).
    if (!item.classList.contains('sf-pv__item--zoomable')) return;
    const img = item.querySelector('img.product-media__image, img');
    if (!(img instanceof HTMLElement)) return;

    // If a different item is already zoomed, ignore this pinch — most likely
    // a stray finger/palm landed off-slide and elementFromPoint resolved to
    // somewhere else. We don't want to wipe the active zoom's pan state.
    if (this.#pinchItem && this.#pinchItem !== item && this.#pinchScale > 1) {
      return;
    }

    // Always use centered transform-origin so pan math (clamping to ±overflow/2)
    // is symmetric and the image can be dragged to any edge.
    img.style.transformOrigin = '50% 50%';

    // If switching to a different item from a non-zoomed state, drop pan state.
    if (this.#pinchItem && this.#pinchItem !== item) {
      this.#panX = 0;
      this.#panY = 0;
    }

    this.#pinchActive = true;
    this.#pinchItem = item;
    this.#pinchImage = img;
    this.#pinchStartDist = this.#pinchDistance(e.touches);
    this.#pinchStartScale = this.#pinchScale;
    item.dataset.pinching = 'true';
    item.dataset.pinchZoomed = 'true';
    void midX; void midY; // pinch midpoint used for hit-test only
  };

  #updatePinch = (e) => {
    if (!this.#pinchActive || !this.#pinchImage) return;
    if (e.touches.length < 2) return;
    if (e.cancelable) e.preventDefault();

    const dist = this.#pinchDistance(e.touches);
    if (!this.#pinchStartDist) return;
    const ratio = dist / this.#pinchStartDist;
    const maxScale = parseFloat(getComputedStyle(this).getPropertyValue('--sf-zoom-scale')) || 2.5;
    const scale = Math.max(1, Math.min(maxScale, this.#pinchStartScale * ratio));
    this.#pinchScale = scale;
    this.#clampPan();
    this.#applyPinchTransform();
  };

  #updatePan = (e) => {
    if (!this.#panActive || !this.#pinchImage) return;
    if (e.touches.length < 1) return;
    if (e.cancelable) e.preventDefault();
    const dx = e.touches[0].clientX - this.#panStartTouchX;
    const dy = e.touches[0].clientY - this.#panStartTouchY;
    this.#panX = this.#panStartX + dx;
    this.#panY = this.#panStartY + dy;
    this.#clampPan();
    this.#applyPinchTransform();
  };

  // Keep the scaled image from being dragged past its own edges (no empty
  // space inside the slide bounds).
  #clampPan = () => {
    const item = this.#pinchItem;
    const img = this.#pinchImage;
    if (!item || !img) return;
    const itemRect = item.getBoundingClientRect();
    if (!itemRect.width || !itemRect.height) return;
    const overflowX = (itemRect.width * (this.#pinchScale - 1)) / 2;
    const overflowY = (itemRect.height * (this.#pinchScale - 1)) / 2;
    this.#panX = Math.max(-overflowX, Math.min(overflowX, this.#panX));
    this.#panY = Math.max(-overflowY, Math.min(overflowY, this.#panY));
  };

  #applyPinchTransform = () => {
    if (!this.#pinchImage) return;
    this.#pinchImage.style.transform = `translate(${this.#panX}px, ${this.#panY}px) scale(${this.#pinchScale})`;
  };

  #endPinch = () => {
    if (!this.#pinchActive) return;
    this.#pinchActive = false;
    this.#pinchStartDist = 0;
    // If the gesture pinched all the way back to natural size, fully reset.
    if (this.#pinchScale <= 1.02) {
      this.#resetPinchZoom();
      return;
    }
    // Otherwise: keep the zoom held, but drop the no-transition flag so a
    // subsequent reset animates smoothly.
    if (this.#pinchItem) delete this.#pinchItem.dataset.pinching;
  };

  #resetPinchZoom = () => {
    const item = this.#pinchItem;
    const img = this.#pinchImage;
    this.#pinchActive = false;
    this.#panActive = false;
    this.#pinchScale = 1;
    this.#pinchStartScale = 1;
    this.#pinchStartDist = 0;
    this.#panX = 0;
    this.#panY = 0;
    this.#pinchItem = null;
    this.#pinchImage = null;
    if (item) {
      delete item.dataset.pinching;
      delete item.dataset.pinchZoomed;
    }
    if (img) {
      img.style.transform = '';
      const cleanup = () => {
        img.style.transformOrigin = '';
        img.removeEventListener('transitionend', cleanup);
      };
      img.addEventListener('transitionend', cleanup);
    }
  };

  // ── Inline magnifier zoom (desktop) ────────────────────────────

  #wireZoom = () => {
    // A gallery can hold dozens of slides; only the slides currently in view keep
    // their .sf-pv__zoom button tabbable, so keyboard users are not forced through
    // one tab stop per hidden slide. The thumbnail rail brings any slide into view.
    this.#zoomTabObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const button = entry.target.querySelector('.sf-pv__zoom');
          if (button instanceof HTMLElement) button.tabIndex = entry.isIntersecting ? 0 : -1;
        }
      },
      // Viewport root: on desktop the stack box contains every slide, so it
      // would report all of them as visible.
      { root: null, threshold: 0.5 }
    );

    for (const item of this.refs.items || []) {
      if (!item.classList.contains('sf-pv__item--zoomable')) continue;
      item.addEventListener('click', this.#onZoomClick);
      item.addEventListener('pointermove', this.#onZoomPointerMove);
      item.addEventListener('pointerleave', this.#onZoomPointerLeave);
      this.#zoomTabObserver.observe(item);
    }
  };

  #unwireZoom = () => {
    this.#zoomTabObserver?.disconnect();
    this.#zoomTabObserver = null;
    for (const item of this.refs.items || []) {
      item.removeEventListener('click', this.#onZoomClick);
      item.removeEventListener('pointermove', this.#onZoomPointerMove);
      item.removeEventListener('pointerleave', this.#onZoomPointerLeave);
    }
  };

  #onZoomClick = (event) => {
    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;

    // On mobile, require a DOUBLE tap to activate (or close) the zoom. A
    // single tap does nothing — avoids accidental zooms while scrolling.
    if (this.#mobileMedia.matches) {
      const now = performance.now();
      const isDoubleTap =
        this.#lastTapItem === item && now - this.#lastTapTime < DOUBLE_TAP_MS;
      if (isDoubleTap) {
        this.#lastTapTime = 0;
        this.#lastTapItem = null;
        this.#mobileTapZoom(item);
      } else {
        this.#lastTapTime = now;
        this.#lastTapItem = item;
      }
      return;
    }

    if (this.#zoomedItem === item) {
      this.#resetZoom(item);
      return;
    }
    if (this.#zoomedItem) this.#resetZoom(this.#zoomedItem);
    this.#zoomedItem = item;
    item.dataset.zoomed = 'true';

    // Keyboard activation (Enter/Space on the .sf-pv__zoom button) fires a click
    // with no pointer position; zoom into the center of the slide in that case.
    if (event.detail === 0) {
      const rect = item.getBoundingClientRect();
      this.#updateZoomOrigin(item, {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      });
      return;
    }
    this.#updateZoomOrigin(item, event);
  };

  #mobileTapZoom = (item) => {
    // Tap on the same already-zoomed item → close.
    if (this.#pinchItem === item && this.#pinchScale > 1) {
      this.#resetPinchZoom();
      return;
    }
    // Only zoom items explicitly marked zoomable (images, never videos).
    if (!item.classList.contains('sf-pv__item--zoomable')) return;
    // Different item already zoomed → reset before re-zooming this one.
    if (this.#pinchItem && this.#pinchItem !== item) {
      this.#resetPinchZoom();
    }
    const img = item.querySelector('img.product-media__image, img');
    if (!(img instanceof HTMLElement)) return;
    const maxScale = parseFloat(getComputedStyle(this).getPropertyValue('--sf-zoom-scale')) || 2.5;
    img.style.transformOrigin = '50% 50%';
    this.#pinchItem = item;
    this.#pinchImage = img;
    this.#pinchScale = maxScale;
    this.#pinchStartScale = maxScale;
    this.#panX = 0;
    this.#panY = 0;
    item.dataset.pinchZoomed = 'true';
    this.#applyPinchTransform();
  };

  #onZoomPointerMove = (event) => {
    const item = event.currentTarget;
    if (this.#zoomedItem !== item) return;
    cancelAnimationFrame(this.#zoomRaf);
    this.#zoomRaf = requestAnimationFrame(() => this.#updateZoomOrigin(item, event));
  };

  #onZoomPointerLeave = (event) => {
    // On touch devices the pointerleave fires when the finger lifts — keep zoom
    // active so the user can tap again to close. Only auto-reset on mouse.
    if (event.pointerType === 'touch') return;
    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;
    if (this.#zoomedItem === item) this.#resetZoom(item);
  };

  #updateZoomOrigin = (item, event) => {
    const rect = item.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    // Clamp to [0, 100] so the image can't visually fly past the slide edges
    // when the pointer/finger drifts outside the slide bounds (the "bounce").
    const rawX = ((event.clientX - rect.left) / rect.width) * 100;
    const rawY = ((event.clientY - rect.top) / rect.height) * 100;
    const x = Math.max(0, Math.min(100, rawX));
    const y = Math.max(0, Math.min(100, rawY));
    const img = item.querySelector('img.product-media__image, img');
    if (img instanceof HTMLElement) {
      img.style.transformOrigin = `${x}% ${y}%`;
    }
  };

  #resetZoom = (item) => {
    item.dataset.zoomed = 'false';
    const img = item.querySelector('img.product-media__image, img');
    if (img instanceof HTMLElement) img.style.transformOrigin = '';
    if (this.#zoomedItem === item) this.#zoomedItem = null;
  };

  // Auto-start the first video when the product has video media in the first slot.
  #maybeAutostartVideo = () => {
    if (!this.dataset.firstIsVideo) return;
    const firstItem = this.refs.items?.[0];
    if (!firstItem) return;
    const deferredMedia = firstItem.querySelector('deferred-media');
    if (!deferredMedia) return;
    customElements.whenDefined('deferred-media').then(() => {
      if (typeof deferredMedia.showDeferredMedia === 'function') {
        deferredMedia.showDeferredMedia();
      }
    });
  };

  #initVideoAutoplay = () => {
    const options = {
      root: this.refs.stack,
      threshold: 0.6
    };

    this.#videoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const deferredMedia = entry.target.querySelector('deferred-media');

        if (entry.isIntersecting) {
          // First entry: showDeferredMedia() clones the template and starts
          // playback with iOS-safe timing inside loadContent. Calling play()
          // again here would race the deferred RAF and leave iOS stuck on a
          // black frame — only resume directly when the video is already in
          // the DOM from a previous entry.
          if (deferredMedia?.getAttribute('data-media-loaded')) {
            entry.target.querySelector('video')?.play().catch(() => {});
          } else if (typeof deferredMedia?.showDeferredMedia === 'function') {
            deferredMedia.showDeferredMedia();
          }
        } else {
          entry.target.querySelector('video')?.pause();
        }
      }
    }, options);

    this.refs.items?.forEach(item => {
      if (item.querySelector('deferred-media, video, external-video')) {
        this.#videoObserver.observe(item);
      }
    });
  };
}

if (!customElements.get('sf-product-media-scroll')) {
  customElements.define('sf-product-media-scroll', SfProductMediaScroll);
}
